/**
 * WalletConnectingPanel.jsx — Panel WALLET_CONNECTING.
 *
 * Affiche le stepper 3 étapes pendant le flow SIWE :
 *   Étape 1 — "Connexion au portefeuille"     (wagmi connectAsync)
 *   Étape 2 — "En attente de signature"        (signMessageAsync EIP-4361)
 *   Étape 3 — "Vérification"                   (POST /auth/verify-signature)
 *
 * `activeStep` est dérivé des actions dispatchées par useWalletConnect :
 *   - WALLET_CONNECTED  → étape 2 active
 *   - SIGNATURE_OBTAINED → étape 3 active
 *   - VERIFY_SUCCESS_* / ERROR → stepper terminé (le panel est démonté)
 *
 * État erreur (state ERROR ou error != null) :
 *   Remplace le stepper par un bloc AlertBox + bouton Réessayer + bouton Annuler.
 *
 * Règles DA respectées :
 * §14 — Zéro import @mui/@material-ui.
 * §20 — Zéro littéral couleur en dur.
 * §18 ter — ≥1 breakpoint (hérité des styled-components partagés).
 * §12 undecies — Casse grammaticale (pas d'UPPERCASE forcé).
 * §8.7 — Aucun import Redux.
 *
 * Props consommées :
 *   stateMachineState {object}   — state complet de useWalletStateMachine
 *   dispatch          {function} — dispatch de useWalletStateMachine
 *   cancelConnect     {function} — (useWalletConnect) annule + déconnecte wagmi
 *   supportedWallets  {string[]} — pour retrouver le nom+icône du wallet sélectionné
 */

import React, { useEffect, useRef, useState } from 'react';
import { ACTION_TYPES }       from '../hooks/useWalletStateMachine';
import { WALLET_CONNECTORS_META, NETWORK_LABEL } from '../config/wagmiConfig';
import {
  WalletBody,
  WalletFooter,
  StepperWrap,
  StepRow,
  StepBadge,
  StepText,
  StepTitle,
  StepSub,
  WalletIcon,
  WalletName,
  SecondaryBtn,
  DangerBtn,
  AlertBox,
  AlertText,
} from '../styles/EarniWalletModal.styles';
import { Spinner } from '../../ui/ModalDA';

/* ─── Définition des 3 étapes du stepper ────────────────────────────────────── */

const STEPS = [
  {
    id:      'connect',
    title:   'Connecting wallet',
    sub:     'Open your wallet and approve the connection.',
  },
  {
    id:      'sign',
    title:   'Waiting for signature',
    sub:     'Sign the message in your wallet to authenticate.',
  },
  {
    id:      'verify',
    title:   'Verifying',
    sub:     'Verifying your identity on the server…',
  },
];

/* ─── Icônes SVG inline ──────────────────────────────────────────────────────── */

/** Checkmark — étape terminée. */
const CheckIcon = () => (
  <svg width="0.75rem" height="0.75rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Croix d'erreur — état erreur. */
const ErrorIcon = () => (
  <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

/** Triangle avertissement réseau. */
const NetworkIcon = () => (
  <svg width="1.25rem" height="1.25rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/* ─── Dérivation de l'activeStep depuis les actions ─────────────────────────── */

/**
 * Retourne l'index (0-based) de l'étape active selon l'état et les événements reçus.
 * Utilise une ref pour mémoriser le dernier event significatif.
 *
 * @param {string}  currentState
 * @param {string|null} lastAction   — dernière action reçue ('WALLET_CONNECTED', 'SIGNATURE_OBTAINED', …)
 * @returns {number} 0 | 1 | 2
 */
function deriveActiveStep(currentState, lastAction) {
  if (currentState !== 'WALLET_CONNECTING') return 0;
  if (lastAction === 'SIGNATURE_OBTAINED')  return 2;
  if (lastAction === 'WALLET_CONNECTED')    return 1;
  return 0;
}

/* ─── Bloc erreur ────────────────────────────────────────────────────────────── */

/**
 * ErrorBlock — affiche l'erreur + boutons Réessayer / Annuler.
 *
 * @param {object}   props
 * @param {object}   props.error         — { message, code, recoverable }
 * @param {function} props.onRetry
 * @param {function} props.onCancel
 */
function ErrorBlock({ error, onRetry, onCancel }) {
  const isNetworkMismatch = error?.code === 'NETWORK_MISMATCH';
  const isUserRejected    = error?.code === 'USER_REJECTED';

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
    >
      <AlertBox $type="error" aria-label="Connection error">
        <div style={{ flexShrink: 0, color: 'inherit' }}>
          {isNetworkMismatch ? <NetworkIcon /> : <ErrorIcon />}
        </div>
        <div>
          <AlertText $type="error" as="p" style={{ fontWeight: 700, marginBottom: '0.25rem' }}>
            {isNetworkMismatch
              ? 'Wrong network'
              : isUserRejected
              ? 'Connection rejected'
              : 'Connection error'}
          </AlertText>
          <AlertText $type="error" as="p">
            {error?.message ?? 'An unexpected error occurred.'}
          </AlertText>
          {isNetworkMismatch && (
            <AlertText $type="error" as="p" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
              Switch to the {NETWORK_LABEL} network in your wallet and try again.
            </AlertText>
          )}
        </div>
      </AlertBox>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {error?.recoverable !== false && (
          <SecondaryBtn
            type="button"
            onClick={onRetry}
            aria-label="Try connecting again"
            style={{ flex: 1 }}
          >
            Try again
          </SecondaryBtn>
        )}
        <DangerBtn
          type="button"
          onClick={onCancel}
          aria-label="Cancel connection"
          style={{ flex: 1 }}
        >
          Cancel
        </DangerBtn>
      </div>
    </div>
  );
}

/* ─── Composant principal ───────────────────────────────────────────────────── */

/**
 * WalletConnectingPanel
 *
 * @param {object}   props
 * @param {object}   props.stateMachineState — state de useWalletStateMachine
 * @param {function} props.dispatch          — dispatch
 * @param {function} props.cancelConnect     — hook useWalletConnect
 * @param {string[]} props.supportedWallets  — IDs de connecteurs
 */
export function WalletConnectingPanel({
  stateMachineState,
  dispatch,
  cancelConnect,
}) {
  const { currentState, selectedWalletId, error } = stateMachineState;

  /* Mémorisation de l'étape active — évite les resets lors du re-render */
  const lastActionRef = useRef(null);
  const [activeStep,  setActiveStep] = useState(0);

  /* Surveille les dispatches pour avancer le stepper (via un proxy du dispatch) */
  /* NOTE : on reçoit directement les props, donc on dérive via selectedWalletId
     et connectedAddress présent dans stateMachineState */
  useEffect(() => {
    const { connectedAddress } = stateMachineState;

    if (connectedAddress && lastActionRef.current !== 'WALLET_CONNECTED') {
      lastActionRef.current = 'WALLET_CONNECTED';
      setActiveStep(1);
    }
  }, [stateMachineState]);

  /* Écoute le step SIGNATURE_OBTAINED via un event personnalisé émis par EarniWalletModal */
  useEffect(() => {
    const handler = () => {
      lastActionRef.current = 'SIGNATURE_OBTAINED';
      setActiveStep(2);
    };
    window.addEventListener('earni:signature_obtained', handler);
    return () => window.removeEventListener('earni:signature_obtained', handler);
  }, []);

  /* ── Handlers ─────────────────────────────────────────────────────────────── */

  const handleRetry = () => {
    dispatch({ type: ACTION_TYPES.RETRY });
  };

  const handleCancel = () => {
    cancelConnect();
  };

  /* ── Lookup du connecteur sélectionné ────────────────────────────────────── */
  const connectorMeta = WALLET_CONNECTORS_META.find((c) => c.id === selectedWalletId);
  const walletName  = connectorMeta?.name  ?? 'Wallet';
  const walletIcon  = connectorMeta?.icon  ?? null;

  /* ── Rendu ────────────────────────────────────────────────────────────────── */
  return (
    <>
      <WalletBody>
        {/* En-tête : logo + nom du wallet */}
        <div
          style={{
            display:        'flex',
            flexDirection:  'column',
            alignItems:     'center',
            gap:            '0.5rem',
            paddingBottom:  '0.5rem',
          }}
          aria-label={`Connecting with ${walletName}`}
        >
          {walletIcon ? (
            <WalletIcon
              src={walletIcon}
              alt={walletName}
              style={{ width: '4rem', height: '4rem' }}
            />
          ) : (
            /* Fallback : spinner centré */
            <Spinner style={{ marginBottom: 0 }} />
          )}
          <WalletName style={{ fontSize: '1rem' }}>{walletName}</WalletName>
        </div>

        {/* État erreur : bloc d'alerte */}
        {currentState === 'ERROR' && error ? (
          <ErrorBlock
            error={error}
            onRetry={handleRetry}
            onCancel={handleCancel}
          />
        ) : (
          /* État normal : stepper */
          <StepperWrap
            role="list"
            aria-label="Connection steps"
            aria-live="polite"
          >
            {STEPS.map((step, index) => {
              const isDone   = index < activeStep;
              const isActive = index === activeStep;

              return (
                <StepRow
                  key={step.id}
                  role="listitem"
                  aria-current={isActive ? 'step' : undefined}
                >
                  {/* Badge circulaire : numéro, checkmark ou spinner */}
                  <StepBadge
                    $done={isDone}
                    $active={isActive}
                    aria-hidden="true"
                  >
                    {isDone ? (
                      <CheckIcon />
                    ) : isActive ? (
                      /* Spinner inline pour l'étape active */
                      <span
                        style={{
                          display:    'inline-block',
                          width:      '0.875rem',
                          height:     '0.875rem',
                          border:     '0.1875rem solid rgba(255,255,255,0.3)',
                          borderTop:  '0.1875rem solid #fff',
                          borderRadius: '50%',
                          animation:  'spin 1s linear infinite',
                        }}
                      />
                    ) : (
                      index + 1
                    )}
                  </StepBadge>

                  {/* Texte de l'étape */}
                  <StepText>
                    <StepTitle $active={isActive}>{step.title}</StepTitle>
                    {isActive && (
                      <StepSub>{step.sub}</StepSub>
                    )}
                  </StepText>
                </StepRow>
              );
            })}
          </StepperWrap>
        )}
      </WalletBody>

      {/* Footer — bouton Annuler (disponible hors état erreur) */}
      {currentState !== 'ERROR' && (
        <WalletFooter>
          <SecondaryBtn
            type="button"
            onClick={handleCancel}
            aria-label="Cancel connection"
          >
            Cancel
          </SecondaryBtn>
        </WalletFooter>
      )}

      {/* Keyframes animation spinner inline — injecté une seule fois via style global */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

export default WalletConnectingPanel;
